import api from '@/api';
import teacherAdminApi from '@/api/teacher-admin';

const actions = {
  FETCH_CLASSES: async ({ commit, dispatch, state, rootState }) => {
    const { currentPage, pageSize, ordering, filters = {}, dateRange } = state;
    const offset = currentPage * pageSize;
    const { teacher, ...otherFilters } = filters;

    const params = {
      limit: pageSize,
      offset,
      ordering,
      teacher_name: teacher && teacher.name !== 'All' ? teacher.name : null,
      ...(dateRange ? { ...dateRange } : {}),
      ...otherFilters,
    };

    dispatch('wait/start', 'classes.fetching', { root: true });

    try {
      const { count, results: classes } = await teacherAdminApi.fetchClasses(params);

      if (classes.length > 0) {
        const timeZoneEntry = classes.find(entry => entry.timezone);
        const timeZone = timeZoneEntry ? timeZoneEntry.timezone : null;
        const route = rootState.route.name;

        if (timeZone) {
          commit('timezones/SET_ROUTE_TIMEZONE', { route, timeZone }, { root: true });
        }
      }

      commit('SET_CLASSES', classes);
      commit('SET_TOTAL_COUNT', count);
      commit('SET_REFRESH_DATE', Date.now());
    } finally {
      dispatch('wait/end', 'classes.fetching', { root: true });
    }
  },

  FETCH_TEACHERS: async ({ commit, dispatch }) => {
    dispatch('wait/start', 'fetching teachers', { root: true });

    try {
      const teachers = await teacherAdminApi.fetchTeachers();

      commit('SET_TEACHERS', teachers);
    } finally {
      dispatch('wait/end', 'fetching teachers', { root: true });
    }
  },

  FETCH_ORGANIZATIONS: async ({ commit }) => {
    const organizations = await api.fetchOrganizations();

    commit('SET_ORGANIZATIONS', organizations);
  },

  FETCH_FEATURE_GROUPS: async ({ commit, dispatch }, organizationId) => {
    if (!organizationId) {
      return;
    }

    dispatch('wait/start', 'classes.groups', { root: true });

    try {
      const featureGroups = await api.fetchFeatureGroups(organizationId);

      commit('SET_FEATURE_GROUPS', featureGroups);
    } finally {
      dispatch('wait/end', 'classes.groups', { root: true });
    }
  },

  FETCH_LEVELS: async ({ commit, dispatch }, organizationId) => {
    if (!organizationId) {
      return;
    }

    dispatch('wait/start', 'classes.levels', { root: true });

    try {
      const levels = await api.fetchOrganizationLevels(organizationId);

      commit('SET_LEVELS', levels);
    } finally {
      dispatch('wait/end', 'classes.levels', { root: true });
    }
  },

  FETCH_TYPES_AND_POLICIES: async ({ commit }) => {
    const formOptions = await teacherAdminApi.fetchFormOptions();

    if (!formOptions) {
      return;
    }

    commit('SET_CLASS_TYPES', formOptions.class_types);
    commit('SET_CLASS_POLICIES', formOptions.policies);
  },

  DOWNLOAD_CLASSES: async ({ state, rootGetters }, format) => {
    const { id: userId } = rootGetters.userProfile;

    if (!userId) {
      return;
    }

    const fields = state.columns.filter(column => column.showColumn).map(column => column.source_field);
    const { currentPage, pageSize, ordering, filters, dateRange } = state;
    const offset = currentPage * pageSize;
    const params = {
      limit: pageSize,
      offset,
      ordering,
      ...(dateRange ? { ...dateRange } : {}),
      ...filters,
      format,
      include: fields,
    };
    await teacherAdminApi.downloadClasses(userId, params);
  },

  BULK_DELETE_CLASSES: async ({ commit, dispatch, rootGetters }, classes) => {
    const { id: userId } = rootGetters.userProfile;

    if (!userId || !classes.length) {
      return;
    }

    dispatch('wait/start', 'classes.delete', { root: true });
    commit('SET_DATE_RANGE', null);

    try {
      await teacherAdminApi.deleteClasses(userId, classes);
    } finally {
      dispatch('wait/end', 'classes.delete', { root: true });
      dispatch('FETCH_CLASSES');
    }
  },

  APPLY_FILTER: ({ commit, dispatch }, filter) => {
    commit('SET_FILTER', filter);
    dispatch('FETCH_CLASSES');
  },

  RESET_FILTERS: ({ commit, dispatch }) => {
    commit('RESET_FILTERS');
    dispatch('FETCH_CLASSES');
  },

  CHANGE_ORDERING: ({ commit, dispatch, state }, { ordering }) => {
    if (ordering === state.ordering) {
      return;
    }

    commit('SET_ORDERING', ordering);
    commit('SET_CURRENT_PAGE', 0);
    dispatch('FETCH_CLASSES');
  },

  CHANGE_PAGE_SIZE: ({ commit, dispatch }, { pageSize }) => {
    commit('SET_PAGE_SIZE', pageSize);
    commit('SET_CURRENT_PAGE', 0);
    dispatch('FETCH_CLASSES');
  },

  CHANGE_CURRENT_PAGE: ({ commit, dispatch }, { currentPage }) => {
    commit('SET_CURRENT_PAGE', currentPage);
    dispatch('FETCH_CLASSES');
  },

  CHANGE_DATE_RANGE: ({ commit, dispatch }, dateRange) => {
    commit('SET_DATE_RANGE', dateRange);
    commit('SET_CURRENT_PAGE', 0);
    dispatch('FETCH_CLASSES');
  },

  EDIT_ENROLLMENT: async ({ dispatch }, { classId, payload }) => {
    dispatch('wait/start', 'classes.editEnrollment', { root: true });

    try {
      await teacherAdminApi.editEnrollment(classId, payload);
      dispatch('FETCH_CLASSES');
    } finally {
      dispatch('wait/end', 'classes.editEnrollment', { root: true });
    }
  },
};

export default actions;
